/**
 *  @file       protocol/connection/identity-cache.js
 *  @author     Nazila Akhavan, Nazila@kingsds.network
 *  @date       January 2020
 *
 * The identity cache is used to ensure that for any connection baseHref and identity are match,
 * then insert them into the cache (browser/ node). It is also used to clear the cache.
 *
 * Does not use localstorage for disk because we want users to be able to manually edit this
 * file if a remote changes their private key.
 */
'use strict';

const IDENTITY_CACHE_LOCAL_STORAGE_KEY = 'idCache';
const env = require('dcp/common/dcp-env');
const { fs, requireNative } = require('dcp/dcp-client/webpack-native-bridge');
const { Address } = require('dcp/dcp-client/wallet');
const debugging = require('dcp/debugging').scope('dcp');
const fsBasic = require('dcp/common/fs-basic');

exports.getGlobalIdentityCache = getGlobalIdentityCache;

class IdentityCache
{
  constructor()
  {
    this.identities = { /* baseHref: address */ };

    if (env.isBrowserPlatform)
    {
      try
      {
        this.identities = JSON.parse(window.localStorage.getItem(IDENTITY_CACHE_LOCAL_STORAGE_KEY)) || {};
        this.initIdentities();
      }
      catch (error)
      {
        debugging() && console.warn('Failed to parse identity cache from local storage', error);
      }
    }
    else if (fsBasic.fileExists(this.cacheFilePath))
    {
      const cacheFile = fsBasic.readFile(this.cacheFilePath);
      try
      {
        this.identities = JSON.parse(cacheFile);
      }
      catch(error)
      {
        if (require('dcp/build').config.build === 'debug')
          console.error('corrupted identity cache file contents:', Array.from(cacheFile).map(x=>x.charCodeAt(0).toString(16)));
        throw error;
      }
      this.initIdentities();
    }
  }

  get cacheFolder()
  {
    return require('dcp/utils/resource-dir').getResourceDir();
  }

  get cacheFilePath()
  {
    return fsBasic.pathResolve(this.cacheFolder, dcpConfig.dcp.identityCache?.filename || 'identity-cache.json');
  }

  /**
   * Initialize Address instances for the identities after they are
   * retrieved from the cache.
   */
  initIdentities()
  {
    for (let baseHref in this.identities)
      this.identities[baseHref] = new Address(this.identities[baseHref]);
  }

  /**
   * Learn a new identity.  In DCP, we always assume that the first identity we see for a given baseHref
   * is the authoritative one, unless contradicted at some point in the future by a presharedAddress. In
   * the case where the the psA is in conflict with what we already knew, we emit a log warning.
   *
   * @param {object} url                a URL containing the base href (origin and pathname) associated
   *                                    with this identity
   * @param {object} identity           instance of eth::Address
   * @param {object} presharedAddress   optional - address which is shared out-of-band (eg https) that we
   *                                    *know* is safe for this resource and so can override our extant
   *                                    knowledge
   *
   * @return false if what we have learned is in conflict with what's already in the cache (and not psA)
   *         true  if we were allowed to learn that this is the identity associated with
   *
   */
  learnIdentity (url, identity, presharedAddress)
  {
    const baseHref = url.origin + url.pathname;

    if (presharedAddress && identity.eq(presharedAddress))
    {
      if (!this.identities.hasOwnProperty(baseHref) || !identity.eq(this.identities[baseHref]))
      {
        /* This should never happen unless the server has changed its private key, or we are in a MITM attack */
        if (this.identities[baseHref])
          console.warn(`**** Warning: preshared peer address ${presharedAddress} replaces ${this.identities[baseHref]} in identity cache ****`);
        this.identities[baseHref] = String(identity);
      }
      return true;
    }

    if (this.identities.hasOwnProperty(baseHref))
      return identity.eq(this.identities[baseHref]);

    this.identities[baseHref] = String(identity);
    this.saveIdentities();
    return true;
  }

  /**
   * Return the identity associated with a given URL.
   *
   * @param {object} url                a URL containing the base href (origin and pathname) for
   *                                    the identity we seek
   */
  getIdentity(url)
  {
    const baseHref = url.origin + url.pathname;
    return this.identities[baseHref];
  }

  /** Save identities to the local storage or identity-cache.json */
  saveIdentities()
  {
    if (false
        || dcpConfig.dcp.identityCache?.disable === true
        || dcpConfig.dcp.identityCache?.disable === 'write'
        || env.getenv('DCP_IDENTITY_CACHE_WRITE_DISABLE'))
      return;
    if (env.isBrowserPlatform)
      window.localStorage.setItem('idCache', JSON.stringify(this.identities, null, 2));
    else
    {
      try
      {
        if (!fsBasic.dirExists(this.cacheFolder))
          fsBasic.mkdir(this.cacheFolder);
        fsBasic.writeFileAtomic(this.cacheFilePath, JSON.stringify(this.identities));
      } catch (e) {
        debugging() && console.error('Failed to save identity cache to file:', e.message);
      }
    }
  }

  clearAll ()
  {
    this.identities = { };
    this.saveIdentities();
  }

  clearIdentity (id)
  {
    for (let baseHref in this.identities)
      if (this.identities[baseHref].eq(id))
        delete this.identities[baseHref];
    this.saveIdentities();
  }
}

/* API */
function getGlobalIdentityCache()
{
  if (!getGlobalIdentityCache.cache)
    getGlobalIdentityCache.cache = new IdentityCache();

  return getGlobalIdentityCache.cache;
}
